import { Suspense } from 'react';
import { Await } from 'react-router';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage } from 'react-intl';
import {
  EventTime,
  firstLetterCapStyle,
  type EventSession,
} from '~/ui/event-time';

/** View model for a card's date row, keyed by experience id in the loader's
 *  `datesByExperience` map. */
export type NextEventInfo = {
  /** More than one upcoming event — or an open-hours experience with bookable
   *  slots — → show the "More available times" hint instead of a specific
   *  date. */
  hasMoreDates: boolean;
  /** The sessions of the single upcoming event, when there is exactly one —
   *  used to render the actual date/time. Null when there are zero or multiple
   *  upcoming events. */
  sessions: EventSession[] | null;
};

/** Shared per-experience next-event map from the home loader: the map itself
 *  when the loader waited for it (small storefronts), a promise when it streams
 *  in after the page (large ones) — see `DEFER_NEXT_EVENTS_THRESHOLD`. */
export type DatesByExperience =
  Map<string, NextEventInfo> | Promise<Map<string, NextEventInfo>>;

type Props = {
  experienceId: string;
  datesByExperience: DatesByExperience;
};

/**
 * The experience card's date row. Renders one of three states from the (possibly
 * deferred) next-event data:
 *  - more than one upcoming event, or an open-hours experience with bookable
 *    slots → the "More available times" hint;
 *  - exactly one upcoming event → that event's actual date/time;
 *  - none (or the fetch failed) → nothing.
 *
 * The per-experience next-events lookup is the loader's most expensive fetch, so
 * large storefronts defer it to the client. This resolves the shared promise via
 * `<Await>` and shows a skeleton bar until it streams in.
 *
 * A settled map skips `<Await>`, which suspends the first time it sees a
 * promise, even a resolved one: a boundary forced to hydrate synchronously then
 * throws its server HTML away and renders again from scratch. The boundary
 * itself stays either way: any hydration mismatch the row's own
 * `suppressHydrationWarning` doesn't cover then stays in this row instead of
 * re-rendering the whole page.
 */
export function ExperienceCardDates({
  experienceId,
  datesByExperience,
}: Props) {
  return (
    <Suspense fallback={<DateRowSkeleton />}>
      {datesByExperience instanceof Map ? (
        <DateRow info={datesByExperience.get(experienceId)} />
      ) : (
        <Await resolve={datesByExperience} errorElement={null}>
          {(map) => <DateRow info={map.get(experienceId)} />}
        </Await>
      )}
    </Suspense>
  );
}

function DateRow({ info }: { info: NextEventInfo | undefined }) {
  if (info?.hasMoreDates) return <MoreTimesLabel />;
  if (info?.sessions?.length) return <NextDateLabel sessions={info.sessions} />;
  return null;
}

/** The single upcoming event's date/time, in the card's subtitle style. Exported
 *  so the card's stories can render this state without wiring a promise. */
export function NextDateLabel({ sessions }: { sessions: EventSession[] }) {
  return (
    <EventTime sessions={sessions}>
      {({ title, details }) => (
        <Text
          as="span"
          style="body-small-regular"
          color="tertiary"
          className={firstLetterCapStyle}
          suppressHydrationWarning
        >
          {title}
          {details ? ` · ${details}` : ''}
        </Text>
      )}
    </EventTime>
  );
}

/** The resolved "More available times" hint. Exported so the card's stories can
 *  render the loaded state without wiring a promise. */
export function MoreTimesLabel() {
  return (
    <Text as="span" style="body-small-regular" color="tertiary">
      <FormattedMessage
        defaultMessage="More available times"
        description="Date row on an experience card when there is more than one upcoming event, or when the experience has open operating hours to book within"
      />
    </Text>
  );
}

/** Placeholder shown while the deferred next-events data streams in. Mirrors the
 *  neutral-bar skeleton used elsewhere (e.g. the terms dialog). */
export function DateRowSkeleton() {
  return <span className={skeletonBar} aria-hidden />;
}

const skeletonBar = css({
  display: 'block',
  height: 'medium',
  width: '[140px]',
  maxWidth: 'full',
  borderRadius: 'small',
  background: 'surface.neutral',
});
